/**
 * "Found six things — which of these are right?"
 *
 * The review step *is* the feature. A model looking at a shelf will merge two
 * jars, invent a third, and mislabel a fourth, and dropping that straight into
 * someone's inventory would poison the thing the whole app depends on being
 * true. So nothing is written until it has been looked at.
 *
 * Everything is editable in place: untick what isn't there, fix a name, move a
 * category. That is faster than adding six items by hand and honest about the
 * model being a first draft rather than an oracle.
 */

import { useState } from "react";

import { CATEGORY_RULES } from "../features/food/categories";
import { FOOD_CATEGORIES } from "../features/food/types";
import type { FoodCategory } from "../features/food/types";
import type { Recognition } from "../features/vision/types";
import { CATEGORY_ICONS, Icon } from "./Icon";
import { Sheet } from "./Sheet";

import "./ScanReview.css";

/** One row, as the user has edited it. */
export interface ReviewRow {
  keep: boolean;
  name: string;
  category: FoodCategory;
  shelfLifeDays: number;
}

interface ScanReviewProps {
  found: Recognition[];
  /** The photo they all came from, attached to whatever gets kept. */
  photo: string;
  onConfirm: (rows: ReviewRow[], photo: string) => void;
  onClose: () => void;
}

export function ScanReview({ found, photo, onConfirm, onClose }: ScanReviewProps) {
  const [rows, setRows] = useState<ReviewRow[]>(() =>
    found.map((result) => ({
      keep: true,
      name: result.name,
      category: result.category,
      shelfLifeDays: result.shelfLifeDays,
    }))
  );

  const update = (index: number, patch: Partial<ReviewRow>) =>
    setRows((current) =>
      current.map((row, i) => (i === index ? { ...row, ...patch } : row))
    );

  const keeping = rows.filter((row) => row.keep);
  // A blank name would be unfindable, so it blocks the whole add rather than
  // being silently dropped along with whatever else was on that row.
  const blank = keeping.some((row) => row.name.trim().length === 0);

  return (
    <Sheet title={`Found ${found.length}`} icon="camera" onClose={onClose}>
      <p className="scan__intro">
        Untick anything that isn't there, and fix anything it got wrong.
      </p>

      <ul className="scan__rows">
        {rows.map((row, index) => (
          <li className="scan__row" key={index} data-dropped={!row.keep || undefined}>
            <button
              className="scan__keep"
              type="button"
              aria-pressed={row.keep}
              aria-label={`Keep ${row.name || "this item"}`}
              onClick={() => update(index, { keep: !row.keep })}
            >
              <Icon name={row.keep ? "check" : "close"} className="scan__keep-icon" />
            </button>

            <span className="scan__fields">
              <input
                className="scan__name"
                value={row.name}
                aria-label="Name"
                disabled={!row.keep}
                onChange={(event) => update(index, { name: event.target.value })}
              />
              <span className="scan__category">
                <Icon
                  name={CATEGORY_ICONS[row.category]}
                  className="scan__category-icon"
                  data-category={row.category}
                />
                <select
                  className="scan__select"
                  value={row.category}
                  aria-label="Category"
                  disabled={!row.keep}
                  onChange={(event) =>
                    update(index, { category: event.target.value as FoodCategory })
                  }
                >
                  {FOOD_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {CATEGORY_RULES[category].label}
                    </option>
                  ))}
                </select>
              </span>
            </span>
          </li>
        ))}
      </ul>

      {/*
        The item the model missed.

        A shelf read merges two jars and drops a third; the review exists
        because of that. Being able to untick a wrong row but not add a missing
        one only fixed half of it — and the other half sent you back to the
        picker to type it in from scratch, with the shelf still open in front of
        you.
      */}
      <button
        className="scan__add-row"
        type="button"
        onClick={() =>
          setRows((current) => [
            ...current,
            { keep: true, name: "", category: "other", shelfLifeDays: 7 },
          ])
        }
      >
        <Icon name="plus" className="scan__add-row-icon" />
        Add a missing item
      </button>

      {blank && (
        <p className="scan__error" role="alert">
          <Icon name="alert" className="scan__error-icon" />
          Every kept item needs a name.
        </p>
      )}

      <div className="scan__actions">
        <button className="scan__cancel" type="button" onClick={onClose}>
          Cancel
        </button>
        <button
          className="scan__confirm"
          type="button"
          disabled={keeping.length === 0 || blank}
          onClick={() => onConfirm(keeping, photo)}
        >
          {keeping.length === 0
            ? "Nothing selected"
            : `Add ${keeping.length} item${keeping.length === 1 ? "" : "s"}`}
        </button>
      </div>
    </Sheet>
  );
}
